import type { CSSProperties } from "react";
import { Plate } from "./plate";
import { splitTitle } from "@/lib/plate-and-hairline";

/**
 * A page heading rendered as a plate, with its parenthetical demoted.
 *
 * This is the rollout unit. The pilot on /sounds/[id] hand-assembled the
 * heading, and repeating that assembly per surface would mean re-deciding the
 * same three things every time: which part goes on the plate, whether the
 * heading's text content survives, and what the descriptor looks like. Those
 * are system decisions, not page decisions, so they live here.
 *
 * Deliberately NOT a client component. It renders one (Plate), but nothing
 * here needs the browser, so a server page keeps its heading server-rendered
 * and only the sizing crosses the boundary.
 *
 * The heading's TEXT CONTENT is preserved exactly, spacing and parentheses
 * included. Several of these headings are the page's SEO surface; what a
 * crawler reads is not a styling decision. Callers that pass a title with a
 * parenthetical get the same string back in the DOM, only laid out differently.
 */

export interface PlateHeadingProps {
  /** The full title, parenthetical and all. Split happens here. */
  children: string;
  /** Heading level. Defaults to h1; pass h2 for section headings. */
  as?: "h1" | "h2";
  /**
   * Server-pass container widths, in px, at the mobile (390) and desktop
   * (1440) references. The server solves the plate for both and the markup
   * carries both, so what paints before hydration is the size the client
   * settles on — provided these match the column the heading really has.
   * They are worth measuring, not guessing: a heading whose column is not
   * the default (see DEFAULT_USABLE_* in plate.tsx) re-solves after
   * hydration and moves everything under it. Measure with
   * `scripts/qa/plate-sweep.mjs --json` (`containerWidth`) at each viewport.
   */
  usable?: number;
  usableDesktop?: number;
  /**
   * @deprecated No longer selects a breakpoint; both are solved. Accepted so
   * existing call sites compile. Prefer `usable` + `usableDesktop`.
   */
  cap?: number;
  className?: string;
  style?: CSSProperties;
  /** Colour of the demoted parenthetical. Defaults to the muted token. */
  descriptorColor?: string;
  /** Live signal level 0..1 for rule 3. Omit where there is no audio. */
  level?: number;
}

export function PlateHeading({
  children,
  as: Heading = "h1",
  cap,
  usable,
  usableDesktop,
  className,
  style,
  descriptorColor = "var(--tls-fg-muted)",
  level,
}: PlateHeadingProps) {
  const { display, remainder } = splitTitle(children);

  return (
    <Heading
      data-ls
      className={className}
      style={{
        // Longhand, and before the spread, so a caller's `style` can override
        // one edge without restating the others. Note that a margin UTILITY
        // CLASS cannot override this: an inline style outranks any class, so
        // `className="mb-4"` on this component is silently dead. Spacing is a
        // style prop here for exactly that reason.
        marginTop: 0,
        marginBottom: 0,
        color: "var(--tls-fg-strong)",
        fontFamily: "var(--ls-font), var(--tls-font-display)",
        ...style,
      }}
    >
      <Plate
        as="span"
        text={display}
        cap={cap}
        usable={usable}
        usableDesktop={usableDesktop}
        level={level}
      />
      {/*
        The remainder is the ORIGINAL bytes (separator whitespace and
        parentheses included) rendered verbatim, never re-assembled from the
        descriptor. `display + remainder` is exactly the (trimmed) title, so
        this heading's textContent still reconstructs it character for
        character. That is the SEO guarantee; do not template this string.
      */}
      {remainder ? (
        // §17.9: paired with Plate's own `ls-trim` (plate.tsx), `ls-trim` here
        // makes the class's 8px margin-top measure glyph-edge (plate baseline)
        // to glyph-edge (descriptor cap), not box-edge to box-edge — the two
        // sit on ls-space-2 (8px) of the 4px grid for real, rather than for
        // roughly that plus whatever half-leading each font size carries.
        // The type itself is `.ls-plate-descriptor` in plate-and-hairline.css,
        // shared with HeroSection so the two cannot drift apart again.
        <span
          className="ls-trim ls-plate-descriptor"
          style={{ "--ls-descriptor-color": descriptorColor } as CSSProperties}
        >
          {remainder}
        </span>
      ) : null}
    </Heading>
  );
}
